import {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Category } from 'types/Category';
import Header from 'common/components/Header/Header';
import useItem from 'common/hooks/useItem';
import useRouter from 'common/hooks/use-router';
import useList from 'common/hooks/useList';
import FilteredList from 'common/components/Table/FilteredList';

import AlertsContext from 'common/providers/alerts';
import { MdOutlineCategory } from 'react-icons/md';
import CategoryItem from './CategoryItem';

function Categories() {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error: errorList1,
  } = useList<Category>('categories', {
    defaultSorts: ['updatedAt:DESC', 'createdAt:DESC'],
    defaultSort: 'order:ASC',
    overrideItemsPerPage: 10,
  });

  useEffect(() => {
    if (errorList1) {
      setAlert(errorList1, 'danger');
    }
  }, [errorList1, setAlert]);

  const { removeItem } = useItem<Category>('categories', '');

  const { navigate } = useRouter();

  const handleNavigate = (id: any) => {
    navigate(`/categories/edit/${id}`);
  };

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, 'danger');
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const columns = useMemo(
    () => [
      /* {
        Header: t("filters.status"),
        headerClasses: "hidden",
        columnClasses: "w-full mb-[-8px]", // mb-[-12px]
        filterName: "status$contains",
        filterComponent: (props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        ),
      }, */
      {
        Header: t('forms.name'),
        filterName: 'name$containsi',
        filterLabel: t('filters.search'),
        columnClasses: 'md:flex-1 max-w-[400px]',
      },
    ],
    [t]
  );

  const setOverallFilter = useCallback(
    (key: string, value: string | number | boolean) => {
      setFilter(key, value);
    },
    [setFilter]
  );

  return (
    <div className="mx-auto max-w-full py-6 px-6 text-slate-500">
      <Header
        title={t('titlePages.categories')}
        subTitle={t('titleLegends.categories')}
        btnTitle={t('newItem.category')}
        btnSubtitle={t('newItem.categoryLegend')}
        Icon={MdOutlineCategory}
        addLink="/categories/add"
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setOverallFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={CategoryItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Categories;
